import { Button } from "@/components/ui/button";
import { ArrowRight, Menu, X } from "lucide-react";
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import jourexMainLogo from "@/assets/jourex-new-logo.png";

interface LandingHeaderProps {
  onLogin: () => void;
  onCTA: () => void;
  onPartner: () => void;
}

const navItems = [
  { label: "Trading Center", hash: "features" },
  { label: "Community Center", hash: "community" },
  { label: "Affiliate Program", hash: "partners", highlight: true },
  { label: "Market Insights", hash: "insights" },
  { label: "Plans & Products", route: "/pricing" },
  { label: "About Us", hash: "about" },
];

export const LandingHeader = ({ onCTA }: LandingHeaderProps) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const onLanding = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNavClick = (e: React.MouseEvent, item: typeof navItems[number]) => {
    e.preventDefault();
    setMobileOpen(false);

    // Route-based item (Plans & Products)
    if (item.route) {
      if (location.pathname !== item.route) {
        navigate(item.route);
      }
      return;
    }

    // Hash-based item: scroll on landing, navigate to /#hash otherwise
    if (item.hash) {
      if (onLanding) {
        document.getElementById(item.hash)?.scrollIntoView({ behavior: "smooth" });
      } else {
        navigate(`/#${item.hash}`);
      }
    }
  };

  const buildHref = (item: typeof navItems[number]) => {
    if (item.route) return item.route;
    if (item.hash) return onLanding ? `#${item.hash}` : `/#${item.hash}`;
    return "#";
  };

  return (
    <header
      className={`sticky top-0 z-40 w-full transition-all duration-300 ${
        scrolled
          ? "bg-background/80 backdrop-blur-xl border-b border-border/60"
          : "bg-transparent"
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-[170px] lg:h-[182px] py-2">
          {/* Logo */}
          <a href="/" className="flex items-center shrink-0">
            <img
              src={jourexMainLogo}
              alt="JouRex"
              className="h-[154px] lg:h-[166px] w-auto select-none bg-transparent"
              draggable={false}
            />
          </a>

          {/* Center nav */}
          <nav className="hidden lg:flex items-center gap-8">
            {navItems.map((item) => (
              <a
                key={item.label}
                href={buildHref(item)}
                onClick={(e) => handleNavClick(e, item)}
                className={`text-base font-bold transition-colors ${
                  item.highlight
                    ? "text-violet-700 hover:text-violet-900"
                    : "text-foreground hover:text-emerald-700"
                }`}
              >
                {item.label}
              </a>
            ))}
          </nav>

          {/* Right actions */}
          <div className="hidden lg:flex items-center gap-3">
            <Button
              onClick={onCTA}
              className="h-10 px-5 rounded-lg gradient-primary text-primary-foreground font-semibold shadow-card hover:shadow-elevated transition-all"
            >
              Enter Trading Center
              <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileOpen((v) => !v)}
            className="lg:hidden p-2 text-foreground"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="lg:hidden border-t border-border/60 py-4 space-y-3">
            {navItems.map((item) => (
              <a
                key={item.label}
                href={buildHref(item)}
                onClick={(e) => handleNavClick(e, item)}
                className="block py-2 text-base font-bold text-foreground hover:text-emerald-700"
              >
                {item.label}
              </a>
            ))}
            <div className="flex flex-col gap-2 pt-3 border-t border-border/60">
              <Button
                onClick={() => {
                  setMobileOpen(false);
                  onCTA();
                }}
                className="w-full gradient-primary text-primary-foreground"
              >
                Enter Trading Center
                <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};
